import { useState, useEffect, memo, useMemo, type CSSProperties } from 'react'
import { useDispatch } from 'react-redux'
import './EditMemberImage.scss'
import classNames from 'classnames'
import { imageSize } from '../../utils/commonMembersPage'
import { errorCodes, getUserFriendlyErrorMessage } from '../../utils/errormessages'
import defaultImageIcon from '../../images/management/clinicMembers/voidPhoto.png'

// setPageStatus is a plain Redux action creator (e.g. store/newMember/newMemberActions#setPageStatus)
// that returns an action object the component dispatches — not a self-dispatching thunk.
type PageStatusAction = { type: string; payload: unknown }

interface EditMemberImageProps {
  style?: CSSProperties
  isVisible?: boolean
  isViewMode?: boolean
  isImageChanged?: boolean
  profileImgInputValue?: string | Blob | null
  setProfileImgInputValue: (value: string | Blob) => void
  setPageStatus: (status: string) => PageStatusAction
  setIsImageChanged: (value: boolean) => void
  className?: string
}

export const EditMemberImage = memo(
  ({
    style,
    isVisible,
    isViewMode,
    isImageChanged,
    profileImgInputValue,
    setProfileImgInputValue,
    setPageStatus,
    setIsImageChanged,
    className,
  }: EditMemberImageProps) => {
    const dispatch = useDispatch()
    const [localImgInputValue, setLocalImgInputValue] = useState(profileImgInputValue)
    const [imgStyleObject, setImgStyleObject] = useState({})
    const [profileImgInputLocalError, setProfileImgInputLocalError] = useState('')

    useEffect(() => {
      if (profileImgInputValue && !isImageChanged) {
        dispatch(setPageStatus('loading'))
        imageSize(profileImgInputValue).then((event) => {
          // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
          if (event.width > event.height) {
            setImgStyleObject({ height: '100%', width: '' })
          } else {
            setImgStyleObject({
              height: '',
              width: '100%',
            })
          }
          setLocalImgInputValue(profileImgInputValue)
        })
      }
      dispatch(setPageStatus('idle'))
    }, [profileImgInputValue])

    // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
    const onNewImgSelected = (ev) => {
      const allowedExtensions = /(\/jpg|\/jpeg|\/png)$/i
      const allowedExtensionsNameList = ['jpg', 'jpeg', 'png']
      const file = ev.target.files[0]
      if (file) {
        const splittedFileName = file.name.split('.')
        if (file.size > 10485760) {
          setProfileImgInputLocalError(getUserFriendlyErrorMessage(errorCodes.TOO_BIG_FILE_SIZE, null, '10 MB'))
          return false
        }
        if (
          !allowedExtensions.exec(file.type) ||
          !allowedExtensionsNameList.find((el) => el === splittedFileName[splittedFileName.length - 1].toLowerCase())
        ) {
          setProfileImgInputLocalError(
            getUserFriendlyErrorMessage(errorCodes.INVALID_FILE_FORMAT, null, '.jpg, .jpeg, .png'),
          )
          return false
        }
        const fr = new FileReader()
        imageSize(URL.createObjectURL(file)).then((e) => {
          // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
          if (e.width > e.height) {
            setImgStyleObject({ height: '100%', width: '' })
          } else {
            setImgStyleObject({ height: '', width: '100%' })
          }
          fr.readAsDataURL(file)
          setProfileImgInputValue(file)
          setLocalImgInputValue(URL.createObjectURL(file))
          setIsImageChanged(true)
          setProfileImgInputLocalError('')
        })
      }
    }
    const editImgStyleBlock = useMemo<CSSProperties>(
      () => ({
        visibility: isVisible ? 'visible' : 'hidden',
        marginRight: isViewMode ? 44 : 81,
        ...style,
      }),
      [isViewMode, isVisible, style],
    )
    return (
      <div style={editImgStyleBlock} className={classNames('edit-member-image-block', {}, [className])}>
        <div className='circularImg'>
          <img
            className={profileImgInputValue ? 'edit-member-image' : 'edit-member-empty-img'}
            style={imgStyleObject}
            src={typeof localImgInputValue === 'string' && localImgInputValue ? localImgInputValue : defaultImageIcon}
            alt='defaultImageIcon'
            id='profileIcon'
          />
        </div>
        {!isViewMode && (
          <div className='inputBlock'>
            <label htmlFor='profileImg' className='edit-member-image-text'>
              {profileImgInputValue ? 'Edit profile picture' : 'Add profile picture'}
            </label>
            <input id='profileImg' onChange={onNewImgSelected} type='file' accept='image/png,image/jpeg' />
            <div className='inputBlockErrorMessage'>{profileImgInputLocalError}</div>
          </div>
        )}
      </div>
    )
  },
)
